import React, { useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';

/**
 * Tracks the rendered height of the (fixed-position) wedding-code banner so fixed chrome below it
 * — the sticky nav, the hamburger menu — stacks correctly. Its height is intrinsic to its copy, so
 * it measures itself rather than using a constant. Page layout does not depend on this number:
 * `EventGroupCodeBannerSlot` reserves the banner's space in flow.
 */
type EventGroupCodeBannerOffsetContextValue = Readonly<{
  weddingBannerHeight: number;
  setWeddingBannerHeight: (height: number) => void;
}>;

// Uses the shared createContext (not React's) for missing-provider observability; strict:false so
// reads outside a provider return undefined (handled by the no-op default in the hook below).
const [EventGroupCodeBannerOffsetContextProvider, useEventGroupCodeBannerOffsetContext] = createContext<EventGroupCodeBannerOffsetContextValue>({
  name: 'EventGroupCodeBannerOffset',
  strict: false,
});

export const EventGroupCodeBannerOffsetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [weddingBannerHeight, setWeddingBannerHeight] = useState(0);

  const value = useMemo(() => {
    return { weddingBannerHeight, setWeddingBannerHeight };
  }, [weddingBannerHeight]);

  return <EventGroupCodeBannerOffsetContextProvider value={value}>{children}</EventGroupCodeBannerOffsetContextProvider>;
};

/**
 * Returns the current wedding-banner height and a setter. Safe to call without the provider —
 * height reads as 0 and the setter is a no-op — so consumers never need to guard for it.
 */
export const useEventGroupCodeBannerOffset = (): EventGroupCodeBannerOffsetContextValue => {
  return useEventGroupCodeBannerOffsetContext() ?? { weddingBannerHeight: 0, setWeddingBannerHeight: () => undefined };
};
